// Wire de `loan-offerings-business` ↔ contrato del tab Especiales. Todo campo del wire es opcional:
// los textos caen a cadena vacía y los alcances ausentes a lista vacía, que es «todo».
//
// Las palancas del wire viven en DOS grupos —`hardening` endurece, `easing` afloja— y el backend
// rechaza la misma palanca en los dos. La pantalla las junta en una sola con dirección.
import type {
    CreateStorePolicyRequest,
    StorePolicyEasingWire,
    StorePolicyHardeningWire,
    StorePolicyLogEntryResponse,
    StorePolicyResponse,
    UpdateStorePolicyRequest,
} from '@/api/services/loan-offerings/contract';
import { centsToPesos, decimalToPoints, pesosToCents, pointsToDecimal } from '../logic';
import type { PolicyDraft, PolicyLevers, SpecialPolicy, StorePolicyLogEntry } from '../types';

/** Las palancas de los dos grupos del wire, ya en las unidades de la pantalla. */
function toLevers(
    hardening: StorePolicyHardeningWire | undefined,
    easing: StorePolicyEasingWire | undefined,
): PolicyLevers {
    const levers: PolicyLevers = {};
    if (hardening?.minSciScore !== undefined) {
        levers.sciMin = { value: hardening.minSciScore, direction: 'harden' };
    }
    if (hardening?.minDownPaymentPct !== undefined) {
        levers.downMinPct = {
            value: decimalToPoints(hardening.minDownPaymentPct),
            direction: 'harden',
        };
    } else if (easing?.minDownPaymentPct !== undefined) {
        levers.downMinPct = {
            value: decimalToPoints(easing.minDownPaymentPct),
            direction: 'ease',
        };
    }
    if (hardening?.tnaAnnualFloor !== undefined) {
        levers.tna = { value: decimalToPoints(hardening.tnaAnnualFloor), direction: 'harden' };
    } else if (easing?.tnaAnnual !== undefined) {
        levers.tna = { value: decimalToPoints(easing.tnaAnnual), direction: 'ease' };
    }
    if (hardening?.maxFinancedAmountCents !== undefined) {
        levers.maxAmount = {
            value: centsToPesos(hardening.maxFinancedAmountCents),
            direction: 'harden',
        };
    } else if (easing?.maxFinancedAmountCents !== undefined) {
        levers.maxAmount = {
            value: centsToPesos(easing.maxFinancedAmountCents),
            direction: 'ease',
        };
    }
    return levers;
}

/** Una política del wire al modelo de la pantalla. */
export function toSpecialPolicy(wire: StorePolicyResponse): SpecialPolicy {
    return {
        id: wire.policyId ?? '',
        name: wire.name ?? '',
        description: wire.description ?? '',
        suspend: wire.suspended ?? false,
        levers: toLevers(wire.hardening, wire.easing),
        allowedSkus: wire.allowedSkus ?? [],
        targetLevels: wire.targetLevels ?? [],
        storeIds: wire.storeIds ?? [],
        productSkus: wire.targetSkus ?? [],
        segmentIds: wire.segmentIds ?? [],
        validFrom: wire.validFrom ?? null,
        validTo: wire.validUntil ?? null,
        // Un estado ausente se lee borrador: es el único que no promete nada al motor.
        status: wire.status ?? 'DRAFT',
        phase: wire.phase ?? null,
        version: wire.version ?? 0,
        reason: wire.reason ?? null,
        createdBy: wire.createdBy ?? '',
    };
}

/** Un asiento de la bitácora del wire al modelo de la pantalla. */
export function toStorePolicyLogEntry(wire: StorePolicyLogEntryResponse): StorePolicyLogEntry {
    return {
        policyId: wire.policyId ?? '',
        policyName: wire.policyName ?? null,
        action: wire.action ?? 'UPDATE',
        fieldChanged: wire.fieldChanged ?? null,
        oldValue: wire.oldValue ?? null,
        newValue: wire.newValue ?? null,
        occurredAt: wire.occurredAt ?? '',
        performedBy: wire.performedBy ?? '',
        performedByName: wire.performedByName ?? null,
        reason: wire.reason ?? null,
        versionFrom: wire.versionFrom ?? null,
        versionTo: wire.versionTo ?? null,
    };
}

/** El grupo que ENDURECE, armado con las palancas del borrador que apuntan para ese lado. */
export function toHardening(levers: PolicyLevers): StorePolicyHardeningWire {
    const wire: StorePolicyHardeningWire = {};
    // `sciMin` no tiene par en `easing`: el contrato solo la expone como piso.
    if (levers.sciMin !== undefined) wire.minSciScore = Math.round(levers.sciMin.value);
    if (levers.downMinPct?.direction === 'harden') {
        wire.minDownPaymentPct = pointsToDecimal(levers.downMinPct.value);
    }
    if (levers.tna?.direction === 'harden') {
        wire.tnaAnnualFloor = pointsToDecimal(levers.tna.value);
    }
    if (levers.maxAmount?.direction === 'harden') {
        wire.maxFinancedAmountCents = pesosToCents(levers.maxAmount.value);
    }
    return wire;
}

/** El grupo que AFLOJA. `sciMin` nunca entra aquí: el backend no acepta un SCI que afloje. */
export function toEasing(levers: PolicyLevers): StorePolicyEasingWire {
    const wire: StorePolicyEasingWire = {};
    if (levers.downMinPct?.direction === 'ease') {
        wire.minDownPaymentPct = pointsToDecimal(levers.downMinPct.value);
    }
    if (levers.tna?.direction === 'ease') {
        wire.tnaAnnual = pointsToDecimal(levers.tna.value);
    }
    if (levers.maxAmount?.direction === 'ease') {
        wire.maxFinancedAmountCents = pesosToCents(levers.maxAmount.value);
    }
    return wire;
}

/**
 * El borrador completo al alta. La política nace en DRAFT: el estado no viaja. Los alcances vacíos
 * se OMITEN — el contrato los declara `minItems: 1` y ausente ya significa «todo».
 */
export function toCreateRequest(draft: PolicyDraft, reason?: string): CreateStorePolicyRequest {
    const body: CreateStorePolicyRequest = {
        name: draft.name.trim(),
        suspended: draft.suspend,
        hardening: toHardening(draft.levers),
        easing: toEasing(draft.levers),
    };
    if (draft.description.trim() !== '') body.description = draft.description.trim();
    if (draft.allowedSkus.length > 0) body.allowedSkus = draft.allowedSkus;
    if (draft.storeIds.length > 0) body.storeIds = draft.storeIds;
    if (draft.targetLevels.length > 0) body.targetLevels = draft.targetLevels;
    if (draft.productSkus.length > 0) body.targetSkus = draft.productSkus;
    if (draft.segmentIds.length > 0) body.segmentIds = draft.segmentIds;
    if (draft.validFrom !== null) body.validFrom = draft.validFrom;
    if (draft.validTo !== null) body.validUntil = draft.validTo;
    if (reason !== undefined && reason.trim() !== '') body.reason = reason.trim();
    return body;
}

function sameList(left: readonly string[], right: readonly string[]): boolean {
    return left.length === right.length && left.every((value, i) => value === right[i]);
}

/**
 * SOLO lo que cambió. El PUT es un parche parcial y cada campo presente entra a la bitácora, así
 * que mandar el objeto entero asentaría campos que el operador ni tocó.
 *
 * Los dos grupos de palancas van juntos o no van: el backend los REEMPLAZA enteros, y mandar solo
 * uno dejaría vivo el del otro lado y chocaría con la validación de contradicción.
 *
 * Una lista que se vacía viaja como `null` y no como `[]`: es la forma de volver a «toda la red».
 */
export function toUpdateRequest(
    original: PolicyDraft,
    draft: PolicyDraft,
    reason?: string,
): UpdateStorePolicyRequest {
    const patch: UpdateStorePolicyRequest = {};
    if (original.name !== draft.name) patch.name = draft.name.trim();
    if (original.description !== draft.description) patch.description = draft.description.trim();
    if (original.suspend !== draft.suspend) patch.suspended = draft.suspend;
    if (original.validFrom !== draft.validFrom) patch.validFrom = draft.validFrom;
    if (original.validTo !== draft.validTo) patch.validUntil = draft.validTo;
    if (!sameList(original.allowedSkus, draft.allowedSkus)) {
        patch.allowedSkus = draft.allowedSkus.length > 0 ? draft.allowedSkus : null;
    }
    if (!sameList(original.storeIds, draft.storeIds)) {
        patch.storeIds = draft.storeIds.length > 0 ? draft.storeIds : null;
    }
    if (!sameList(original.targetLevels, draft.targetLevels)) {
        patch.targetLevels = draft.targetLevels.length > 0 ? draft.targetLevels : null;
    }
    if (!sameList(original.productSkus, draft.productSkus)) {
        patch.targetSkus = draft.productSkus.length > 0 ? draft.productSkus : null;
    }
    if (!sameList(original.segmentIds, draft.segmentIds)) {
        patch.segmentIds = draft.segmentIds.length > 0 ? draft.segmentIds : null;
    }
    const hardening = toHardening(draft.levers);
    const easing = toEasing(draft.levers);
    if (
        JSON.stringify(toHardening(original.levers)) !== JSON.stringify(hardening) ||
        JSON.stringify(toEasing(original.levers)) !== JSON.stringify(easing)
    ) {
        patch.hardening = hardening;
        patch.easing = easing;
    }
    if (reason !== undefined && reason.trim() !== '') patch.reason = reason.trim();
    return patch;
}
